'use client';

import type { Address } from '@ahm/api-wrappers-core';
import { useState, useEffect, useRef, useCallback } from 'react';
import { getGeocodeReverseFn } from '@/api/ahafood/get-revert-address-fn';
import { calculateDistance } from '@/lib/fast-location-utils';

const DISTANCE_THRESHOLD_METERS = 200;
const INIT_DELAY_MS = 2000;

/**
 * Silently compares live GPS position against the app-provided address.
 * Shows a nudge if the user has moved more than DISTANCE_THRESHOLD_METERS away.
 * Only runs once per component lifecycle (hasChecked ref).
 */
export function useLocationNudge(appAddress: Address | null) {
  const [showNudge, setShowNudge] = useState(false);
  const [detectedAddress, setDetectedAddress] = useState<Address | null>(null);
  const hasChecked = useRef(false);

  useEffect(() => {
    if (hasChecked.current || !appAddress?.lat || !appAddress?.lng) return;
    if (typeof navigator === 'undefined' || !('geolocation' in navigator))
      return;

    const timer = setTimeout(() => {
      hasChecked.current = true;

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const liveLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          const distance = calculateDistance(
            { lat: appAddress.lat!, lng: appAddress.lng! },
            liveLoc
          );

          if (distance > DISTANCE_THRESHOLD_METERS) {
            try {
              const liveAddress = await getGeocodeReverseFn(liveLoc);
              if (liveAddress) {
                setDetectedAddress(liveAddress);
                setShowNudge(true);
              }
            } catch {
              // Silent fail — nudge is an optional enhancement
            }
          }
        },
        () => {
          // Permission denied or unavailable — silent fail
        },
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 60 * 1000,
        }
      );
    }, INIT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [appAddress]);

  const dismissNudge = useCallback(() => setShowNudge(false), []);

  const acceptNudge = useCallback((): Address | null => {
    setShowNudge(false);
    return detectedAddress;
  }, [detectedAddress]);

  return { showNudge, detectedAddress, dismissNudge, acceptNudge };
}
